import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { LoadingSmall } from "@/components/LoadingSmall";
import { useTranslation } from "react-i18next";
import { Switch } from "@/components/ui/switch";
import { DatePicker } from "@/components/DatePicker";
import { parse } from "date-fns";
import { t } from "i18next";
import { CalendarWeekDaysSubForm } from "@/components/calendar/form/CalendarWeekDaysSubForm";
import { AssignableMediaTypes, PlayerMediaSelectRaw } from "@/pages/player/form/PlayerMediaSelectRaw";
import { PlayerContentGroupSelect } from "@/pages/player/form/PlayerContentGroupSelect";
import { ContentGroupType } from "@/lib/types";

const formSchema = z
  .object({
    periodStart: z.string().min(1),
    periodEnd: z.string().min(1),
    isActive: z.boolean(),
    contentGroupId: z.number().optional().or(z.null()),
  })
  .refine(
    (data) => {
      if (!data.periodStart || !data.periodEnd) return true;
      const start = parse(data.periodStart, "dd-MM-yyyy", new Date());
      const end = parse(data.periodEnd, "dd-MM-yyyy", new Date());
      return end >= start;
    },
    {
      message: t("common.endDateMustBeAfterStartDate"),
      path: ["periodEnd"],
    }
  );

export type FormSchemaType = z.infer<typeof formSchema>;

type Props = {
  buttonText: string;
  onSubmit: (values: FormSchemaType) => void;
  isLoading: boolean;
  defaultValues?: FormSchemaType;
};

export const CampaignRobotItemMetaForm: React.FC<Props> = ({ buttonText, onSubmit, isLoading, defaultValues }) => {
  const { t } = useTranslation();

  const form = useForm<FormSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues || {
      periodStart: "",
      periodEnd: "",
      isActive: true,
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="max-h-[90vh] overflow-auto mt-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="w-full md:w-1/2 space-y-8">
            <FormField
              control={form.control}
              name="periodStart"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("common.startDateOrig")}</FormLabel>
                  <FormControl>
                    <div>
                      <DatePicker value={field.value} onChange={field.onChange} />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="periodEnd"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("common.endDateOrig")}</FormLabel>
                  <FormControl>
                    <div>
                      <DatePicker value={field.value} onChange={field.onChange} />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("campaign.isActive")}</FormLabel>
                  <FormControl>
                    <div>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="contentGroupId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("entities.contentGroup")}</FormLabel>
                  <FormControl>
                    <PlayerMediaSelectRaw
                      value={{
                        contentGroupId: field.value,
                      }}
                      onChange={(value, type) => {
                        field.onChange(value.contentGroupId);
                      }}
                      visibleTypes={["contentGroup"]}
                      customFilter={(contentGroup) => contentGroup.type === ContentGroupType.Robot}
                      enabledTypes={["contentGroup"]}
                      onClear={() => {
                        field.onChange(null);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        <Button type="submit" disabled={isLoading} className="mt-4">
          {buttonText}
          {isLoading && <LoadingSmall />}
        </Button>
      </form>
    </Form>
  );
};
